/**
 * Parsing Review Workstation
 * Main component for efficient parsing error review
 *
 * Features:
 * - Queue list sidebar for item selection
 * - Side-by-side layout: Original document + correction panel
 * - Smart input: Natural language parsing
 * - Keyboard navigation
 * - Dynamic type switching
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DocumentViewer from './DocumentViewer/DocumentViewer';
import SmartInput from './CorrectionPanel/SmartInput/SmartInput';
import FieldEditor from './CorrectionPanel/FieldEditor/FieldEditor';
import TypeSelector from './CorrectionPanel/TypeSelector';
import ActionBar from './ActionBar/ActionBar';
import QueueDrawer from './QueueNav/QueueDrawer';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { useQueueState } from './hooks/useQueueState';
import { useIdleDetection } from './hooks/useIdleDetection';
import { validateData, getFieldLabel } from './utils/fieldSchemas';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { useDetachablePanel } from './hooks/useDetachablePanel';

const ParsingReviewWorkstation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { reviewItemId } = useParams();
  const smartInputRef = useRef(null);

  // Queue state management
  const {
    queue,
    filters,
    correction,
    selectedItems,
    isDeleting,
    nextItem,
    prevItem,
    firstItem,
    lastItem,
    goToItem,
    loadQueue,
    loadStats,
    assignItem,
    unassignItem,
    completeReview,
    toggleItemSelection,
    selectAll,
    clearSelection,
    deleteSelectedItems,
    deleteItem,
    updateField,
    updateLeg,
    addLeg,
    removeLeg,
    changeDocumentType,
    applyParsedData,
    setSmartInputValue,
    setNotes,
    resetCorrection,
    updateFilters
  } = useQueueState(user?.id);

  // UI state
  const [documentZoom, setDocumentZoom] = useState(100);
  const [documentViewMode, setDocumentViewMode] = useState('html');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);

  // Load specific item from URL
  useEffect(() => {
    if (reviewItemId) {
      goToItem(reviewItemId);
    }
  }, [reviewItemId, goToItem]);

  // Computed values
  const currentItem = queue.currentItem;
  const isAssignedToMe = currentItem?.assigned_to_user_id === user?.id;
  const isTerminalStatus = currentItem?.status === 'approved' || currentItem?.status === 'rejected';
  const canApprove = isAssignedToMe && !isTerminalStatus;

  // Idle detection - auto-unassign if reviewer is inactive
  const activeItemId = isAssignedToMe && !isTerminalStatus ? currentItem?.id : null;
  const handleIdle = useCallback(async () => {
    const result = await unassignItem();
    if (result.success) {
      setError('You were unassigned due to inactivity. Click "Assign" to resume.');
    }
  }, [unassignItem]);
  const { isIdle } = useIdleDetection(activeItemId, handleIdle);

  // Handle item selection from queue list
  const handleSelectItem = useCallback((itemId) => {
    goToItem(itemId);
    // Update URL without full page reload
    navigate(`/parsing-review/${itemId}`, { replace: true });
  }, [goToItem, navigate]);

  // Handlers
  const handleAssign = useCallback(async () => {
    setError(null);
    const result = await assignItem();
    if (!result.success) {
      setError(result.error);
    } else {
      setSuccessMessage('Item assigned to you');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  }, [assignItem]);

  const handleApprove = useCallback(async () => {
    if (!canApprove) return;

    setIsSaving(true);
    setError(null);

    const result = await completeReview('approved', {
      correctedData: correction.parsedData,
      notes: correction.notes
    });

    setIsSaving(false);

    if (!result.success) {
      setError(result.error);
    } else {
      setSuccessMessage('Item approved');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  }, [canApprove, completeReview, correction]);

  const handleApproveAndNext = useCallback(async () => {
    await handleApprove();
    // nextItem is called automatically after completeReview
  }, [handleApprove]);

  const handleReject = useCallback(async () => {
    if (!canApprove) return;

    setIsSaving(true);
    setError(null);

    const result = await completeReview('rejected', {
      notes: correction.notes
    });

    setIsSaving(false);

    if (!result.success) {
      setError(result.error);
    } else {
      setSuccessMessage('Item rejected');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  }, [canApprove, completeReview, correction.notes]);

  const handleSkip = useCallback(() => {
    nextItem();
  }, [nextItem]);

  const handleDelete = useCallback(async () => {
    if (!currentItem) return;
    setError(null);
    const result = await deleteItem(currentItem.id);
    if (!result.success) {
      setError(result.error);
    } else {
      setSuccessMessage('Item removed from queue');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  }, [currentItem, deleteItem]);

  const handleSmartInputApply = useCallback((parsedData) => {
    applyParsedData(parsedData);
  }, [applyParsedData]);

  const handleTypeChange = useCallback((newType) => {
    changeDocumentType(newType);
  }, [changeDocumentType]);

  const handleFocusSmartInput = useCallback(() => {
    smartInputRef.current?.focus?.();
  }, []);

  // Keyboard navigation
  useKeyboardNav({
    onNextItem: nextItem,
    onPrevItem: prevItem,
    onFirstItem: firstItem,
    onLastItem: lastItem,
    onAssign: handleAssign,
    onApprove: handleApprove,
    onApproveAndNext: handleApproveAndNext,
    onReject: handleReject,
    onSkip: handleSkip,
    onFocusSmartInput: handleFocusSmartInput,
    onZoomIn: () => setDocumentZoom(z => Math.min(z + 10, 200)),
    onZoomOut: () => setDocumentZoom(z => Math.max(z - 10, 50)),
    onToggleView: () => setDocumentViewMode(m => m === 'html' ? 'text' : 'html'),
    onTypeChange: handleTypeChange,
    onToggleQueue: () => setQueueDrawerOpen(o => !o),
    enabled: !isSaving
  });

  // Detachable panel support
  const {
    detachedPanel,
    isDetached,
    popupBlocked,
    detachPanel,
    reattachPanel,
    setPopupBlocked
  } = useDetachablePanel({
    reviewItemId,
    currentItem,
    correction,
    documentZoom,
    documentViewMode,
    canApprove,
    isAssignedToMe,
    handlers: {
      updateField,
      updateLeg,
      addLeg,
      removeLeg,
      changeDocumentType,
      applyParsedData,
      setSmartInputValue,
      setNotes,
      resetCorrection,
      onApprove: handleApprove,
      onApproveAndNext: handleApproveAndNext,
      onReject: handleReject,
      onSkip: handleSkip,
      onAssign: handleAssign,
      onDelete: handleDelete
    }
  });

  // Priority badge colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left: Back + Title + Navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/parsing-review')}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Back to queue overview"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-base font-semibold text-gray-900">Parsing Review</h1>

            <div className="flex items-center gap-1">
              <button
                onClick={prevItem}
                disabled={queue.currentIndex === 0 || isSaving}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous item (K)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-xs text-gray-600 min-w-[60px] text-center tabular-nums">
                {queue.items.length > 0 ? `${queue.currentIndex + 1} of ${queue.items.length}` : 'Empty'}
              </span>
              <button
                onClick={nextItem}
                disabled={queue.currentIndex >= queue.items.length - 1 || isSaving}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next item (J)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Center: Filters */}
          <div className="flex items-center gap-2">
            <select
              value={filters.status}
              onChange={(e) => updateFilters({ status: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="">All (incl. completed)</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filters.priority}
              onChange={(e) => updateFilters({ priority: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md"
            >
              <option value="">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filters.document_type}
              onChange={(e) => updateFilters({ document_type: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md"
            >
              <option value="">All Types</option>
              <option value="flight">Flight</option>
              <option value="hotel">Hotel</option>
              <option value="car_rental">Car Rental</option>
              <option value="private_flight">Private Flight</option>
              <option value="speaker_meeting">Speaker Meeting</option>
              <option value="occurrence">Event / Booking</option>
              <option value="other">Unclassified</option>
            </select>
          </div>

          {/* Right: Stats + Queue + Refresh */}
          <div className="flex items-center gap-2">
            {queue.stats && (
              <div className="hidden sm:flex items-center gap-2 text-xs">
                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                  {queue.stats.by_status?.pending || 0} pending
                </span>
                {(queue.stats.by_priority?.urgent || 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded">
                    {queue.stats.by_priority.urgent} urgent
                  </span>
                )}
              </div>
            )}

            <button
              onClick={() => setQueueDrawerOpen(true)}
              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md flex items-center gap-1"
              title="Browse queue (Q)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              Queue
            </button>

            <button
              onClick={() => { loadQueue(true); loadStats(); }}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error/Success messages */}
        {error && (
          <div className="mt-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mt-2 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 text-xs rounded-md">
            {successMessage}
          </div>
        )}

        {popupBlocked && (
          <div className="mt-2 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-md flex items-center justify-between">
            Popup was blocked. Please allow popups for this site to detach panels to a separate window.
            <button onClick={() => setPopupBlocked(false)} className="text-amber-500 hover:text-amber-700 ml-2">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </header>

      {/* Queue Drawer */}
      <QueueDrawer
        open={queueDrawerOpen}
        onOpenChange={setQueueDrawerOpen}
        items={queue.items}
        currentItemId={currentItem?.id}
        loading={queue.loading}
        onSelect={handleSelectItem}
        getPriorityColor={getPriorityColor}
        getStatusColor={getStatusColor}
        selectedItems={selectedItems}
        onToggleSelection={toggleItemSelection}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onDeleteSelected={deleteSelectedItems}
        isDeleting={isDeleting}
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {queue.loading && queue.items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="mt-2 text-gray-500">Loading queue...</p>
            </div>
          </div>
        ) : queue.items.length === 0 && !currentItem ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Queue is empty</h3>
              <p className="mt-1 text-gray-500">No items match your current filters</p>
            </div>
          </div>
        ) : !currentItem ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Select an item</h3>
              <p className="mt-1 text-gray-500">Press Q to open the queue or use keyboard navigation</p>
            </div>
          </div>
        ) : detachedPanel === 'viewer' ? (
          /* Editor only — viewer is in a separate window */
          <div className="flex-1 flex flex-col">
            {/* Detached banner */}
            <div className="flex-shrink-0 px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-blue-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Document viewer is in a separate window
              </div>
              <button
                onClick={reattachPanel}
                className="px-2 py-1 text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded"
              >
                Reattach
              </button>
            </div>

            {/* Full-width correction panel */}
            <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
              {/* Panel header */}
              <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <TypeSelector
                    value={correction.documentType}
                    onChange={handleTypeChange}
                    disabled={!canApprove && !isAssignedToMe}
                  />
                  {currentItem && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`px-2 py-0.5 text-xs rounded border ${getPriorityColor(currentItem.priority)}`}>
                        {currentItem.priority}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(currentItem.status)}`}>
                        {currentItem.status?.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                </div>
                {currentItem && (
                  <div className="mt-2 text-xs text-gray-500">
                    <div className="truncate">{currentItem.organization_name}</div>
                    <div className="truncate">{currentItem.email_subject}</div>
                  </div>
                )}
                {currentItem && (() => {
                  const liveValidation = validateData(correction.documentType, correction.parsedData);
                  if (liveValidation.isValid) return null;
                  return (
                    <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                      <div className="text-xs font-medium text-red-800">Issues detected:</div>
                      <ul className="mt-1 text-xs text-red-600 space-y-0.5">
                        {liveValidation.errors.slice(0, 4).map((err, idx) => (
                          <li key={`err-${idx}`}>• {err}</li>
                        ))}
                        {liveValidation.errors.length > 4 && (
                          <li className="text-red-500">+ {liveValidation.errors.length - 4} more</li>
                        )}
                      </ul>
                    </div>
                  );
                })()}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Smart Input</h3>
                  <SmartInput
                    ref={smartInputRef}
                    documentType={correction.documentType}
                    value={correction.smartInputValue}
                    onChange={setSmartInputValue}
                    onApply={handleSmartInputApply}
                    disabled={!canApprove && !isAssignedToMe}
                  />
                </div>
                <FieldEditor
                  documentType={correction.documentType}
                  data={correction.parsedData}
                  confidence={currentItem?.ai_confidence_score ? {} : {}}
                  onChange={updateField}
                  onLegChange={updateLeg}
                  onAddLeg={addLeg}
                  onRemoveLeg={removeLeg}
                  disabled={!canApprove && !isAssignedToMe}
                  showValidation={true}
                />
              </div>
            </div>
          </div>
        ) : detachedPanel === 'editor' ? (
          /* Viewer only — editor is in a separate window */
          <div className="flex-1 flex flex-col">
            {/* Detached banner */}
            <div className="flex-shrink-0 px-4 py-2 bg-green-50 border-b border-green-200 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-green-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Correction editor is in a separate window
              </div>
              <button
                onClick={reattachPanel}
                className="px-2 py-1 text-xs text-green-700 hover:text-green-900 hover:bg-green-100 rounded"
              >
                Reattach
              </button>
            </div>

            {/* Full-width document viewer */}
            <div className="flex-1 bg-white overflow-hidden">
              <DocumentViewer
                html={currentItem?.original_email_html}
                subject={currentItem?.email_subject}
                sender={currentItem?.email_from}
                receivedAt={currentItem?.email_received_at}
                attachments={currentItem?.attachments || []}
                zoom={documentZoom}
                onZoomChange={setDocumentZoom}
                viewMode={documentViewMode}
                onViewModeChange={setDocumentViewMode}
                className="h-full"
              />
            </div>
          </div>
        ) : (
          /* Normal split layout — both panels inline */
          <ResizablePanelGroup direction="horizontal" autoSaveId="parsing-review-panels" className="flex-1">
            {/* Left: Document Viewer */}
            <ResizablePanel defaultSize={55} minSize={30}>
              <div className="h-full bg-white flex flex-col">
                {/* Detach button for viewer */}
                <div className="flex-shrink-0 flex justify-end px-2 pt-1">
                  <button
                    onClick={() => detachPanel('viewer')}
                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Open document viewer in separate window"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                </div>
                <DocumentViewer
                  html={currentItem?.original_email_html}
                  subject={currentItem?.email_subject}
                  sender={currentItem?.email_from}
                  receivedAt={currentItem?.email_received_at}
                  attachments={currentItem?.attachments || []}
                  zoom={documentZoom}
                  onZoomChange={setDocumentZoom}
                  viewMode={documentViewMode}
                  onViewModeChange={setDocumentViewMode}
                  className="flex-1"
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Right: Correction Panel */}
            <ResizablePanel defaultSize={45} minSize={25}>
              <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
                {/* Panel header */}
                <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <TypeSelector
                      value={correction.documentType}
                      onChange={handleTypeChange}
                      disabled={!canApprove && !isAssignedToMe}
                    />

                    <div className="flex items-center gap-2">
                      {currentItem && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-0.5 text-xs rounded border ${getPriorityColor(currentItem.priority)}`}>
                            {currentItem.priority}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(currentItem.status)}`}>
                            {currentItem.status?.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                      {/* Detach button for editor */}
                      <button
                        onClick={() => detachPanel('editor')}
                        className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                        title="Open correction editor in separate window"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Item info */}
                  {currentItem && (
                    <div className="mt-2 text-xs text-gray-500">
                      <div className="truncate">{currentItem.organization_name}</div>
                      <div className="truncate">{currentItem.email_subject}</div>
                    </div>
                  )}

                  {/* Routing log for approved items */}
                  {currentItem?.status === 'approved' && currentItem?.routing_log && (
                    <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="text-xs font-medium text-blue-800 mb-1">
                        Routing Log {currentItem.component_id ? '(component created)' : '(no component!)'}
                      </div>
                      <div className="space-y-0.5">
                        {currentItem.routing_log.map((entry, idx) => (
                          <div key={idx} className={`text-xs flex gap-1 ${
                            entry.status === 'FAILED' ? 'text-red-700' :
                            entry.status === 'ok' ? 'text-green-700' :
                            entry.status === 'warning' || entry.status === 'no_match' ? 'text-amber-700' :
                            'text-blue-700'
                          }`}>
                            <span className="font-mono">{entry.status === 'FAILED' ? '✗' : entry.status === 'ok' ? '✓' : '→'}</span>
                            <span className="font-medium">{entry.step}:</span>
                            <span className="truncate">{entry.detail}</span>
                          </div>
                        ))}
                      </div>
                      {currentItem.routed_to_user_name && (
                        <div className="mt-1 pt-1 border-t border-blue-200 text-xs text-blue-800">
                          EA: {currentItem.routed_to_user_name}
                          {currentItem.routed_to_executive_name && ` → Executive: ${currentItem.routed_to_executive_name}`}
                          {currentItem.is_unassigned && ' (unassigned)'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Approved but no routing log (legacy items) */}
                  {currentItem?.status === 'approved' && !currentItem?.routing_log && (
                    <div className="mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      <div className="text-xs text-gray-600">
                        {currentItem.component_id
                          ? `Component: ${currentItem.component_id}`
                          : 'No routing log available (approved before logging was added)'}
                      </div>
                    </div>
                  )}

                  {/* Issues summary - uses live validation from current field data */}
                  {currentItem && (() => {
                    const liveValidation = validateData(correction.documentType, correction.parsedData);
                    if (liveValidation.isValid) return null;
                    return (
                      <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                        <div className="text-xs font-medium text-red-800">
                          Issues detected:
                        </div>
                        <ul className="mt-1 text-xs text-red-600 space-y-0.5">
                          {liveValidation.errors.slice(0, 4).map((err, idx) => (
                            <li key={`err-${idx}`}>• {err}</li>
                          ))}
                          {liveValidation.errors.length > 4 && (
                            <li className="text-red-500">+ {liveValidation.errors.length - 4} more</li>
                          )}
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Smart Input */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Smart Input
                    </h3>
                    <SmartInput
                      ref={smartInputRef}
                      documentType={correction.documentType}
                      value={correction.smartInputValue}
                      onChange={setSmartInputValue}
                      onApply={handleSmartInputApply}
                      disabled={!canApprove && !isAssignedToMe}
                    />
                  </div>

                  {/* Field Editor */}
                  <FieldEditor
                    documentType={correction.documentType}
                    data={correction.parsedData}
                    confidence={currentItem?.ai_confidence_score ? {} : {}}
                    onChange={updateField}
                    onLegChange={updateLeg}
                    onAddLeg={addLeg}
                    onRemoveLeg={removeLeg}
                    disabled={!canApprove && !isAssignedToMe}
                    showValidation={true}
                  />
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>

      {/* Action Bar */}
      <ActionBar
        currentIndex={queue.currentIndex}
        totalItems={queue.items.length}
        item={currentItem}
        isAssigned={!!currentItem?.assigned_to_user_id}
        isAssignedToMe={isAssignedToMe}
        canApprove={canApprove}
        isSaving={isSaving}
        isDirty={correction.isDirty}
        onAssign={handleAssign}
        onApprove={handleApprove}
        onApproveAndNext={handleApproveAndNext}
        onReject={handleReject}
        onSkip={handleSkip}
        onDelete={handleDelete}
        onReset={resetCorrection}
        notes={correction.notes}
        onNotesChange={setNotes}
      />
    </div>
  );
};

export default ParsingReviewWorkstation;
